import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Box, Typography, Card, Button, Grid, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, TextField, Divider, RadioGroup, Radio, FormControlLabel
} from '@mui/material';
import { Check, Person, Warehouse, Storage, Analytics, AutoAwesome, CreditCard, Payments, Warning } from '@mui/icons-material';
import { subscriptionAPI, default as api } from '../../services/api';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { detectUserLocation } from '../../utils/locationUtils';
import { getPageSEO } from '../../config/seo';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTranslation } from 'react-i18next';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../../services/config';
import StripePayment from '../../components/payment/StripePayment';
import PayPalPayment from '../../components/payment/PayPalPayment';
import PriceSummary from '../../components/payment/PriceSummary';
import CouponInput from '../../components/payment/CouponInput';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const PricingPage = () => {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { getCurrencySymbol, setBaseCurrency } = useCurrency();
  const seo = getPageSEO('pricing');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const [userCountry, setUserCountry] = useState(null);
  const [userCurrency, setUserCurrency] = useState(null);
  const [coupon, setCoupon] = useState('');
  const [couponBreakdown, setCouponBreakdown] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [error, setError] = useState('');

  const isAuthenticated = !!user;

  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentSubscription();
    }
    detectUserCountry();
  }, [isAuthenticated]);

  useEffect(() => {
    if (userCountry !== undefined) {
      fetchPlans();
    }
  }, [userCountry, userCurrency]); // Added userCurrency dependency

  const formatStorage = (storageMB) => {
    if (storageMB === -1) return 'Unlimited';
    if (!storageMB) return 'N/A';

    if (storageMB >= 1024) {
      const gb = (storageMB / 1024).toFixed(1);
      return `${gb} GB`;
    }
    return `${storageMB} MB`;
  };

  const detectUserCountry = async () => {
    try {
      let resolvedCountry = null;
      let resolvedCurrency = null; // FIXED: Added this declaration

      if (isAuthenticated) {
        try {
          const response = await api.get('/companies/current');
          const company = response.data?.company || response.data;
          if (company && company.country) {
            resolvedCountry = company.country;
            resolvedCurrency = company.currency || user?.currency || null;
          }
          if (!resolvedCurrency && user?.currency) {
            resolvedCurrency = user.currency;
          }
        } catch (err) {
          console.warn('Failed to fetch company country on pricing page:', err);
        }
      }

      if (!resolvedCountry) {
        const location = await detectUserLocation();
        resolvedCountry = location.countryCode;
        resolvedCurrency = location.currencyCode || null;
      }

      setUserCountry(resolvedCountry);
      setUserCurrency(resolvedCurrency);
    } catch (error) {
      console.warn('Geolocation failed. Using default pricing.', error);
      setUserCountry(null);
      setUserCurrency(null);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/current');
      if (response.data.success) {
        setCurrentSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error('Failed to fetch current subscription:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const params = new URLSearchParams();
      if (userCountry) params.append('country', userCountry);
      if (isAuthenticated && userCurrency) {
        params.append('currency', userCurrency);
      }

      // FIXED: Use params.toString() instead of just params
      const response = await api.get(`/registration/plans?${params.toString()}`);

      if (response.data.success) {
        const { data: grouped, meta } = response.data;

        let currencyCode = meta?.currency;
        let currencySymbol = meta?.symbol;

        if (isAuthenticated && userCurrency) {
          currencyCode = userCurrency;
          // Use getCurrencySymbol from context instead of undefined function
          currencySymbol = getCurrencySymbol() || meta?.symbol || '£';
        }

        // Use the overridden currency values
        if (currencyCode) {
          setBaseCurrency({
            code: currencyCode,
            symbol: currencySymbol || currencyCode,
            name: currencyCode
          });
        }

        const planMap = {};
        ['monthly', 'quarterly', 'yearly'].forEach(cycle => {
          (grouped[cycle] || []).forEach(plan => {
            const planName = (plan.name || '').split(' ')[0];
            if (!planName) return;
            if (!planMap[planName]) {
              planMap[planName] = {
                id: plan.id,
                name: planName,
                description: plan.description,
                limits: plan.limits,
                features: plan.features,
                trialDays: plan.trialDays,
                currencySymbol: plan.currencySymbol || currencySymbol || '£',
                currency: plan.currency || currencyCode || 'GBP',
                cta: planName === 'Enterprise' ? 'Contact Sales' : 'Buy Plan',
                popular: planName === 'Professional',
                cycles: {}
              };
            }
            planMap[planName].cycles[cycle] = {
              id: plan.id,
              price: plan.price,
              currencySymbol: plan.currencySymbol || currencySymbol || '£',
              currency: plan.currency || currencyCode || 'GBP'
            };
          });
        });

        const fetchedPlans = Object.values(planMap).sort((a, b) => {
          if (a.name === 'Enterprise') return 1;
          if (b.name === 'Enterprise') return -1;
          return (a.cycles?.monthly?.price || 0) - (b.cycles?.monthly?.price || 0);
        });
        setPlans(fetchedPlans);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBasePlanName = (name) => {
    if (!name) return '';
    return name.replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+(monthly|quarterly|yearly)$/i, '').trim();
  };

  const selectedPlanBase = getBasePlanName(selectedUpgradePlan?.name);
  const matchedPlan = plans.find(p => getBasePlanName(p.name) === selectedPlanBase) || selectedUpgradePlan;
  const effectiveCycle = couponBreakdown?.billingCycle ?? billingCycle;
  const matchedCycle = matchedPlan?.cycles?.[effectiveCycle] || matchedPlan?.cycles?.monthly || {};
  const cycleSpecificPlan = {
    ...matchedPlan,
    id: matchedCycle.id || selectedUpgradePlan?.id || matchedPlan?.id,
    price: matchedCycle.price !== undefined ? parseFloat(matchedCycle.price) : parseFloat(selectedUpgradePlan?.price || 0),
    currency: matchedCycle.currency || selectedUpgradePlan?.currency || 'GBP',
    currencySymbol: matchedCycle.currencySymbol || selectedUpgradePlan?.currencySymbol || '£'
  };
  const cycleSpecificPrice = parseFloat(cycleSpecificPlan?.price) || 0;
  const cycleSymbol = cycleSpecificPlan?.currencySymbol || '£';
  const cycleCurrency = cycleSpecificPlan?.currency || 'GBP';

  const monthlyPlan = matchedPlan?.cycles?.monthly || matchedPlan || {};
  const monthlyPrice = parseFloat(monthlyPlan.price) || 0;
  const periodMultiplier = effectiveCycle === 'quarterly' ? 3 : (effectiveCycle === 'yearly' ? 12 : 1);
  const calculatedBaselineOriginal = monthlyPrice * periodMultiplier;
  const baselineOriginalAmount = calculatedBaselineOriginal > 0 ? calculatedBaselineOriginal : cycleSpecificPrice;
  const baselineFinalAmount = cycleSpecificPrice;
  const baselineDiscountAmount = Math.max(0, baselineOriginalAmount - baselineFinalAmount);
  const baselineDiscountPct = baselineOriginalAmount > 0 ? Math.round((baselineDiscountAmount / baselineOriginalAmount) * 100) : 0;
  const baselineDiscountLabel = baselineDiscountAmount > 0 ? `${effectiveCycle === 'yearly' ? 'Annual' : 'Quarterly'} discount (${baselineDiscountPct}%)` : '';

  const handleCloseUpgradeDialog = () => {
    setUpgradeDialogOpen(false);
    setSelectedUpgradePlan(null);
    setCoupon('');
    setCouponBreakdown(null);
    setPaymentMethod('card');
    setError('');
  };

  // Add this after your other helper functions
  const getAddonPrice = (basePriceUSD) => {
    const currency = getCurrencySymbol();
    const rates = {
      '£': 0.79, // GBP
      '$': 1,    // USD
      '€': 0.92, // EUR
      '₨': 280,  // PKR
      'ر.ق': 3.64, // QAR
      'د.إ': 3.67, // AED
      'ر.س': 3.75, // SAR
      '₹': 83,   // INR
      'A$': 1.53, // AUD
      'C$': 1.36, // CAD
    };

    // Get the current currency symbol
    const symbol = getCurrencySymbol();
    const rate = rates[symbol] || 1;
    const convertedPrice = basePriceUSD * rate;

    // Format based on currency type
    let formattedPrice;
    if (symbol === '₨' || symbol === '₹') {
      // For PKR and INR, show whole numbers
      formattedPrice = Math.round(convertedPrice).toLocaleString();
    } else if (symbol === 'ر.ق' || symbol === 'د.إ' || symbol === 'ر.س') {
      // For Middle Eastern currencies
      formattedPrice = convertedPrice.toFixed(2);
    } else {
      // For USD, GBP, EUR etc.
      formattedPrice = convertedPrice.toFixed(2);
    }

    return `${symbol}${formattedPrice}/month`;
  };

  const getAddons = () => {
    // Base prices in USD
    const basePrices = {
      user: 5,
      warehouse: 10,
      storage: 5,
      analytics: 25
    };

    return [
      {
        name: t('website.pricing.addons.items.user'),
        price: t('website.pricing.planLabels.contactSales'),
        icon: <Person />
      },
      {
        name: t('website.pricing.addons.items.warehouse'),
        price: t('website.pricing.planLabels.contactSales'),
        icon: <Warehouse />
      },
      {
        name: t('website.pricing.addons.items.storage'),
        price: t('website.pricing.planLabels.contactSales'),
        icon: <Storage />
      },
      {
        name: t('website.pricing.addons.items.analytics'),
        price: t('website.pricing.planLabels.contactSales'),
        icon: <Analytics />
      }
    ];
  };


  return (
    <WebsiteLayout>
      <SEO {...seo} />
      <HeroSection chip={t('website.pricing.heroTitle')}>
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
            {t('website.pricing.heroTitle')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 4, color: 'primary.contrastText' }}>
            {t('website.pricing.heroSubtitle')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          </Box>
        </Box>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 12 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} alignItems="stretch">
            {plans.map((plan, idx) => {
              const isEnterprise = plan.name === 'Enterprise';
              const isTeam = plan.name === 'Team';
              const isGrowth = plan.name === 'Growth';

              const cardStyles = {
                p: 4,
                pt: 6,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '2px solid',
                borderColor: isTeam ? 'primary.main' : (theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200'),
                position: 'relative',
                bgcolor: isEnterprise
                  ? (theme => theme.palette.mode === 'dark' ? '#0a0a0a' : '#1a1a1a')
                  : (theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white'),
                color: isEnterprise ? 'white' : 'text.primary',
                ...(isGrowth && {
                  background: (theme => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                    : 'linear-gradient(135deg, #f8f9ff 0%, #eef2ff 100%)'),
                  borderColor: 'primary.light',
                }),
                boxShadow: isTeam ? '0 16px 48px rgba(22, 51, 0, 0.15)' : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 24px 64px rgba(0,0,0,0.1)'
                }
              };

              return (
                <Grid item xs={12} sm={6} md={3} key={`${plan.name}`}>
                  <Card sx={cardStyles}>
                    {isTeam && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 20,
                          right: -8,
                          bgcolor: 'secondary.main',
                          color: 'secondary.contrastText',
                          px: 2,
                          py: 0.5,
                          fontSize: 10,
                          fontWeight: 800,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: -8,
                            right: 0,
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid transparent',
                            borderTop: '8px solid #163300'
                          }
                        }}
                      >
                        {t('website.pricing.planLabels.recommended')}
                      </Box>
                    )}

                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: isEnterprise ? 'white' : 'inherit' }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: isEnterprise ? 'rgba(255,255,255,0.7)' : 'text.secondary', mb: 3, minHeight: 40 }}>
                      {plan.description}
                    </Typography>

                    <Box sx={{ mb: 3, minHeight: 48, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: isEnterprise ? 'white' : 'primary.main' }}>
                        {isAuthenticated && (!isEnterprise || currentSubscription?.status === 'trial')
                          ? (plan.name === currentSubscription?.SubscriptionPlan?.name && currentSubscription?.status !== 'trial' ? 'Renew Plan' : 'Select Plan')
                          : t('website.pricing.planLabels.contactSales')}
                      </Typography>
                    </Box>

                    {isAuthenticated && (!isEnterprise || currentSubscription?.status === 'trial') ? (
                      <Button
                        variant={isTeam ? 'contained' : 'outlined'}
                        fullWidth
                        size="large"
                        disabled={plan.name === currentSubscription?.SubscriptionPlan?.name && currentSubscription?.status === 'active'}
                        onClick={() => {
                          setSelectedUpgradePlan(plan);
                          setUpgradeDialogOpen(true);
                        }}
                        sx={{
                          mb: 4,
                          py: 1.5,
                          fontWeight: 800,
                          borderRadius: 2,
                          ...(isTeam && {
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            '&:hover': { bgcolor: 'secondary.dark' },
                          })
                        }}
                      >
                        {plan.name === currentSubscription?.SubscriptionPlan?.name
                          ? (currentSubscription?.status === 'trial' ? 'Select Plan' : (currentSubscription?.status === 'active' ? t('website.pricing.planLabels.currentPlan') : 'Renew Plan'))
                          : 'Select Plan'}
                      </Button>
                    ) : (
                      <Button
                        component={Link}
                        to={isEnterprise ? "/contact" : `/stepwise-register?plan=${plan.id}`}
                        variant={isTeam ? 'contained' : 'outlined'}
                        fullWidth
                        size="large"
                        sx={{
                          mb: 4,
                          py: 1.5,
                          fontWeight: 800,
                          borderRadius: 2,
                          ...(isTeam && {
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            '&:hover': { bgcolor: 'secondary.dark' },
                          }),
                          ...(isEnterprise && {
                            borderColor: 'rgba(255,255,255,0.3)',
                            color: 'white',
                            '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                          })
                        }}
                      >
                        {isEnterprise ? t('website.pricing.planLabels.contactSales') : 'Start Free Trial'}
                      </Button>
                    )}

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: isEnterprise ? 'rgba(255,255,255,0.5)' : 'text.secondary', mb: 2, display: 'block', letterSpacing: 1 }}>
                        {t('website.pricing.featuresList.title')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Check sx={{ color: isTeam ? 'secondary.main' : 'primary.main', fontSize: 18, mt: 0.3 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{plan.limits?.users === -1 ? t('website.pricing.featuresList.unlimited') : plan.limits?.users} {t('website.pricing.featuresList.users')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Check sx={{ color: isTeam ? 'secondary.main' : 'primary.main', fontSize: 18, mt: 0.3 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{plan.limits?.companies === -1 ? t('website.pricing.featuresList.unlimited') : plan.limits?.companies} {t('website.pricing.featuresList.companies')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Check sx={{ color: isTeam ? 'secondary.main' : 'primary.main', fontSize: 18, mt: 0.3 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {plan.limits?.invoices === -1 ? t('website.pricing.featuresList.unlimited') : plan.limits?.invoices} {t('website.pricing.featuresList.invoices')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Check sx={{ color: isTeam ? 'secondary.main' : 'primary.main', fontSize: 18, mt: 0.3 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {plan.limits?.storage === -1 ? t('website.pricing.featuresList.unlimited') : formatStorage(plan.limits?.storage || '5GB')} {t('website.pricing.featuresList.storage')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                        <Check sx={{ color: isTeam ? 'secondary.main' : 'primary.main', fontSize: 18, mt: 0.3 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {plan.limits?.accounts === -1 ? t('website.pricing.featuresList.unlimited') : plan.limits?.accounts || '250'} {t('website.pricing.featuresList.chartOfAccounts')}
                        </Typography>
                      </Box>

                    </Box>
                  </Card>
                </Grid>
              )
            })
            }
          </Grid>
        )}

      </Container>

      {/* AI Conversion Driver */}
      <Box sx={{
        py: { xs: 10, md: 12 },
        background: theme => theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #163300 0%, #0E0F0C 100%)'
          : 'linear-gradient(135deg, #f7ffef 0%, #ffffff 100%)',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 3, fontSize: { xs: 32, md: 48 } }}>
            {t('website.pricing.aiConsultant.title')}
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 5, maxWidth: 700, mx: 'auto', lineHeight: 1.6 }}>
            {t('website.pricing.aiConsultant.subtitle')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              const trigger = document.getElementById('ai-chat-trigger');
              if (trigger) trigger.click();
              setTimeout(() => {
                const input = document.getElementById('ai-chat-input');
                if (input) {
                  input.value = t('website.pricing.aiConsultant.prompt');
                  input.focus();
                }
              }, 500);
            }}
            sx={{
              px: 8, py: 2.5, borderRadius: 4, fontWeight: 800, fontSize: 18,
              boxShadow: theme => `0 20px 40px ${theme.palette.primary.main}40`
            }}
          >
            {t('website.pricing.aiConsultant.cta')}
          </Button>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('website.pricing.trustedBy')}
            </Typography>
          </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 4, md: 8 },
            flexWrap: 'wrap'
          }}>
            <Box
              component="img"
              src="/trusted_business_logos_1778472780884.png"
              alt="Trusted Business Logos"
              sx={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: 80,
                opacity: 0.9
              }}
            />
          </Box>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'secondary.main', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2, color: 'white' }}>
              {t('website.pricing.migration.title')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 600, mx: 'auto' }}>
              {t('website.pricing.migration.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Button variant="contained" size="large" component={Link} to="/contact" sx={{
              fontWeight: 700,
              bgcolor: 'white',
              color: 'secondary.main',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
            }}>
              {t('website.pricing.migration.cta')}
            </Button>
          </Box>
        </Container>
      </Box>

      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.default' : 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2, color: 'text.primary' }}>
              {t('website.pricing.addons.title')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {t('website.pricing.addons.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {/* FIXED: Call getAddons() function */}
            {getAddons().map((addon, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200', bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white' }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: 'secondary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'secondary.contrastText',
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {addon.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {addon.name}
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    {addon.price}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2, color: 'text.primary' }}>
              {t('website.pricing.compare.title')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {t('website.pricing.compare.subtitle')}
            </Typography>
          </Box>
          {!loading && plans.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                <Box component="thead">
                  <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.300' }}>
                    <Box component="th" sx={{ p: 2, textAlign: 'left', fontWeight: 700, fontSize: 16, minWidth: 200 }}>
                      {t('website.pricing.compare.feature')}
                    </Box>
                    {plans.map((plan) => (
                      <Box component="th" key={plan.name} sx={{ p: 2, textAlign: 'center', fontWeight: 700, fontSize: 16 }}>
                        <Box>{plan.name}</Box>
                        <Box sx={{ fontSize: 14, fontWeight: 600, color: 'secondary.main', mt: 1 }}>
                          {t('website.pricing.compare.custom')}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  <Box component="tr" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.100' }}>
                    <Box component="td" colSpan={plans.length + 1} sx={{ p: 2, fontWeight: 700 }}>
                      {t('website.pricing.compare.limits')}
                    </Box>
                  </Box>
                  <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <Box component="td" sx={{ p: 2, fontWeight: 500 }}>{t('website.pricing.featuresList.users')}</Box>
                    {plans.map((plan) => (
                      <Box component="td" key={plan.name} sx={{ p: 2, textAlign: 'center' }}>
                        {plan.limits?.users === -1 ? t('website.pricing.featuresList.unlimited') : plan.limits?.users || 'N/A'}
                      </Box>
                    ))}
                  </Box>
                  <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <Box component="td" sx={{ p: 2, fontWeight: 500 }}>{t('website.pricing.featuresList.companies')}</Box>
                    {plans.map((plan) => (
                      <Box component="td" key={plan.name} sx={{ p: 2, textAlign: 'center' }}>
                        {plan.limits?.companies === -1 ? t('website.pricing.featuresList.unlimited') : plan.limits?.companies || 'N/A'}
                      </Box>
                    ))}
                  </Box>
                  <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <Box component="td" sx={{ p: 2, fontWeight: 500 }}>{t('website.pricing.featuresList.invoices')}</Box>
                    {plans.map((plan) => (
                      <Box component="td" key={plan.name} sx={{ p: 2, textAlign: 'center' }}>
                        {plan.limits?.invoices === -1 ? t('website.pricing.featuresList.unlimited') : plan.limits?.invoices || 'N/A'}
                      </Box>
                    ))}
                  </Box>
                  <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <Box component="td" sx={{ p: 2, fontWeight: 500 }}>{t('website.pricing.featuresList.storage')}</Box>
                    {plans.map((plan) => (
                      <Box component="td" key={plan.name} sx={{ p: 2, textAlign: 'center' }}>
                        {plan.limits?.storage === -1 ? t('website.pricing.featuresList.unlimited') : formatStorage(plan.limits?.storage) || 'N/A'}
                      </Box>
                    ))}
                  </Box>
                  <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <Box component="td" sx={{ p: 2, fontWeight: 500 }}>{t('website.pricing.featuresList.chartOfAccounts')}</Box>
                    {plans.map((plan) => (
                      <Box component="td" key={plan.name} sx={{ p: 2, textAlign: 'center' }}>
                        {plan.limits?.accounts === -1 ? t('website.pricing.featuresList.unlimited') : plan.limits?.accounts || '250'}
                      </Box>
                    ))}
                  </Box>
                  <Box component="tr" sx={{ bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.100' }}>
                    <Box component="td" colSpan={plans.length + 1} sx={{ p: 2, fontWeight: 700 }}>
                      Features
                    </Box>
                  </Box>
                  {['Smart Reconciliation', 'WhatsApp Invoicing', 'VAT/Tax Support', 'Accountant Portal', 'Multi-Branch Support', 'AI Financial Insights', 'Approval Workflows', 'API Access', 'Dedicated Support'].map((feature) => (
                    <Box component="tr" key={feature} sx={{ borderBottom: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.200' }}>
                      <Box component="td" sx={{ p: 2, fontWeight: 500 }}>
                        {feature}
                      </Box>
                      {plans.map((plan) => {
                        const hasFeature =

                          (feature === 'Smart Reconciliation') ||
                          (feature === 'WhatsApp Invoicing') ||
                          (feature === 'VAT/Tax Support') ||

                          // 👥 TEAM+ FEATURES - Available in Team, Growth & Enterprise
                          (feature === 'Accountant Portal' && (plan.name === 'Team' || plan.name === 'Growth' || plan.name === 'Enterprise')) ||
                          (feature === 'API Access' && (plan.name === 'Team' || plan.name === 'Growth' || plan.name === 'Enterprise')) ||
                          (feature === 'AI Financial Insights' && (plan.name === 'Team' || plan.name === 'Growth' || plan.name === 'Enterprise')) ||

                          // 📊 GROWTH+ FEATURES - Available in Growth & Enterprise
                          (feature === 'Multi-Branch Support' && (plan.name === 'Growth' || plan.name === 'Enterprise')) ||
                          (feature === 'Approval Workflows' && (plan.name === 'Growth' || plan.name === 'Enterprise')) ||

                          // 👑 ENTERPRISE ONLY
                          (feature === 'Dedicated Support' && (plan.name === 'Enterprise'));
                        return (
                          <Box component="td" key={plan.name} sx={{ p: 2, textAlign: 'center' }}>
                            {hasFeature ? <Check sx={{ color: 'secondary.main' }} /> : '—'}
                          </Box>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Container>
      </Box>

      <Dialog open={upgradeDialogOpen} onClose={handleCloseUpgradeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Complete Upgrade</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <PriceSummary
              planName={selectedUpgradePlan?.name}
              billingCycle={couponBreakdown?.billingCycle ?? billingCycle}
              originalAmount={couponBreakdown?.originalAmount ?? baselineOriginalAmount}
              discountAmount={couponBreakdown?.discountAmount ?? baselineDiscountAmount}
              finalAmount={couponBreakdown?.finalAmount ?? baselineFinalAmount}
              currency={couponBreakdown?.currency ?? cycleCurrency}
              currencySymbol={couponBreakdown?.currencySymbol ?? cycleSymbol}
              couponCode={couponBreakdown?.couponCode}
              discountSource={couponBreakdown?.discountSource ?? (baselineDiscountAmount > 0 ? 'cycle' : null)}
              discountLabel={couponBreakdown?.discountLabel ?? baselineDiscountLabel}
              discountPercentage={couponBreakdown?.discountPercentage ?? (baselineDiscountAmount > 0 ? baselineDiscountPct : 0)}
              countryCode={userCountry}
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <CouponInput
              planId={cycleSpecificPlan?.id}
              billingCycle={billingCycle}
              isUpgrade={true}
              amount={cycleSpecificPrice}
              currency={cycleCurrency}
              currencySymbol={cycleSymbol}
              onApplied={(breakdown) => {
                setCouponBreakdown(breakdown);
              }}
              onRemoved={() => setCouponBreakdown(null)}
            />
            {couponBreakdown?.note && (
              <Alert
                severity="info"
                icon={<Warning fontSize="small" />}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                {couponBreakdown.note}
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
            <Button
              variant={paymentMethod === 'card' ? 'contained' : 'outlined'}
              onClick={() => setPaymentMethod('card')}
              sx={{
                minWidth: 160,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: paymentMethod === 'card' ? 'secondary.main' : 'transparent',
                color: paymentMethod === 'card' ? 'white' : 'text.primary',
                borderColor: paymentMethod === 'card' ? 'secondary.main' : 'grey.300',
                '&:hover': { bgcolor: paymentMethod === 'card' ? 'secondary.dark' : 'grey.50' }
              }}
            >
              💳 Credit / Debit Card
            </Button>
            <Button
              variant={paymentMethod === 'paypal' ? 'contained' : 'outlined'}
              onClick={() => setPaymentMethod('paypal')}
              sx={{
                minWidth: 160,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: paymentMethod === 'paypal' ? '#0070ba' : 'transparent',
                color: paymentMethod === 'paypal' ? 'white' : 'text.primary',
                borderColor: paymentMethod === 'paypal' ? '#0070ba' : 'grey.300',
                '&:hover': { bgcolor: paymentMethod === 'paypal' ? '#005ea6' : 'grey.50' }
              }}
            >
              🅿 PayPal
            </Button>
          </Box>

          <Card sx={{ p: 4, border: '1px solid', borderColor: 'grey.200', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            {(couponBreakdown && couponBreakdown.finalAmount === 0) ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom color="success.main">
                  Your total is {cycleSymbol}0.00!
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      await api.post('/subscription/activate-upgrade', { planId: cycleSpecificPlan?.id });
                      handleCloseUpgradeDialog();
                      window.location.reload();
                    } catch (e) {
                      setError(e.response?.data?.message || e.message || 'Upgrade failed');
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  Activate Plan
                </Button>
              </Box>
            ) : (
              paymentMethod === 'card' ? (
                <Box sx={{ mt: 3 }}>
                  <Elements stripe={stripePromise}>
                    <StripePayment
                      createIntent={async () => {
                        const response = await api.post('/subscription/change-plan-intent', {
                          planId: cycleSpecificPlan.id,
                          billingCycle: effectiveCycle,
                          couponCode: couponBreakdown?.couponCode
                        });
                        return response.data.data.clientSecret;
                      }}
                      verifyPayment={async (paymentIntentId) => {
                        const response = await api.post('/subscription/change-plan', {
                          paymentIntentId,
                          planId: cycleSpecificPlan.id,
                          couponCode: couponBreakdown?.couponCode
                        });
                        return response.data.success;
                      }}
                      onPaymentComplete={() => {
                        handleCloseUpgradeDialog();
                        window.location.reload();
                      }}
                    />
                  </Elements>
                </Box>
              ) : (
                <PayPalPayment
                  amount={couponBreakdown?.finalAmount ?? cycleSpecificPrice}
                  currency={cycleCurrency}
                  createOrder={async () => {
                    const response = await api.post('/subscription/paypal-create-order', {
                      planId: cycleSpecificPlan.id,
                      billingCycle: effectiveCycle,
                      couponCode: couponBreakdown?.couponCode
                    });
                    return response.data.data.orderId;
                  }}
                  onApprove={async (orderId) => {
                    await api.post('/subscription/change-plan', {
                      paypalOrderId: orderId,
                      planId: cycleSpecificPlan.id,
                      couponCode: couponBreakdown?.couponCode
                    });
                  }}
                  onPaymentComplete={() => {
                    handleCloseUpgradeDialog();
                    window.location.reload();
                  }}
                />
              )
            )}
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpgradeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </WebsiteLayout>
  );
};

export default PricingPage;